import React, { useCallback } from 'react';
import styled from 'styled-components';
import { ButtonGroup, Icon, IconLookup, Tooltip } from 'component-library-and-tools';

import LabeledContent from 'shared/components/LabeledContent';
import TextField from 'shared/components/TextField';
import { WorkflowType } from 'shared/types/workflow';
import { colors } from 'lib/prisma-styles';

import { WorkflowDetailsProps } from './types';
import {
  WORKFLOW_NAME_KEY,
  WORKFLOW_TYPE_KEY,
  WORKFLOW_NAME_MAX_CHARACTERS,
  ShowWorkflowTypeExplanationBlurb,
} from './constants';

const WORKFLOW_NAME_INPUT_CONTAINER_CLASS_NAME = 'workflow-name-input-container';
const WORKFLOW_NAME_INPUT_CLASS_NAME = 'workflow-name-input';

const WORKFLOW_TYPE_BUTTON_CLASS_NAME = 'workflow-type-button';
const WORKFLOW_TYPE_ACTIVE_BUTTON_CLASS_NAME = 'active-workflow-type-button';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledInnerContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;

  &:not(:last-child) {
    margin-bottom: 2.166666667rem;
  }
`;

const StyledWorkflowNameContainer = styled(LabeledContent)`
  width: 100%;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  max-width: 37.083333333rem;

  & {
    .${WORKFLOW_NAME_INPUT_CONTAINER_CLASS_NAME} {
      height: 3.666666667rem;
    }

    .${WORKFLOW_NAME_INPUT_CLASS_NAME} {
      font-size: 1.166666667rem;
    }
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  background-color: ${colors.interactive.defaultBackground};
  padding: 0.666666667rem;
  margin-bottom: 0.666666667rem;

  & .${WORKFLOW_TYPE_BUTTON_CLASS_NAME} {
    height: 3.666666667rem;
    padding: 1rem 1.666666667rem;
    background-color: #fff;
    border-radius: 4px;
    font-size: 1.166666667rem;
    font-weight: 700;
    color: #3369d5;
  }

  & .${WORKFLOW_TYPE_ACTIVE_BUTTON_CLASS_NAME} {
    background-color: ${colors.interactive.primaryAction};
    color: #fff;
  }
`;

const StyledWorkflowTypeMessage = styled.p`
  margin: 1rem 0 0 0;
  color: #8d9199;
  word-break: break-word;
  font-size: 1.15rem;
`;

const StyledWorkflowAdditionalDetails = styled.div``;

const WorkflowDetails = ({
  onChange,
  values,
}: WorkflowDetailsProps) => {
  const selectedWorkflowType = values[WORKFLOW_TYPE_KEY];

  const handleTextInputChange = useCallback((event) => {
    onChange(event.target.value, WORKFLOW_NAME_KEY);
  }, [onChange]);

  const handleButtonClick = useCallback((selectedButton) => {
    onChange(selectedButton.name, WORKFLOW_TYPE_KEY);
  }, [onChange]);

  return (
    <StyledContainer>
      <StyledInnerContainer>
        <StyledWorkflowNameContainer label='Workflow Name'>
          <StyledTextField
            textInputContainerClassName={WORKFLOW_NAME_INPUT_CONTAINER_CLASS_NAME}
            textInputClassName={WORKFLOW_NAME_INPUT_CLASS_NAME}
            onChange={handleTextInputChange}
            value={values.name}
            maxLength={WORKFLOW_NAME_MAX_CHARACTERS}
          />
        </StyledWorkflowNameContainer>
      </StyledInnerContainer>
      <StyledInnerContainer>
        <LabeledContent label='Workflow Type'>
          <StyledButtonGroup
            buttons={[
              { name: WorkflowType.INDICATOR_PRIORITIZATION, label: 'Indicator Prioritization' },
            ]}
            buttonClassName={WORKFLOW_TYPE_BUTTON_CLASS_NAME}
            activeButtonClassName={WORKFLOW_TYPE_ACTIVE_BUTTON_CLASS_NAME}
            onClick={handleButtonClick}
            defaultActiveButton={selectedWorkflowType}
          />
        </LabeledContent>
        {selectedWorkflowType && (
          <StyledWorkflowAdditionalDetails>
            <StyledWorkflowTypeMessage>
              {ShowWorkflowTypeExplanationBlurb()}
              <WorkflowHelpDocsLink />
            </StyledWorkflowTypeMessage>
          </StyledWorkflowAdditionalDetails>

        )}
      </StyledInnerContainer>
    </StyledContainer>
  );
};

// Helper Components
const StyledHelpDocsTextAndIcon = styled.a`
  display: inline-flex;
  align-items: baseline;

  margin-left: 0.35rem;
  color: inherit;
  font-weight: 700;

  > .icon {
    margin-right: 0.35rem;
    font-weight: inherit;
  }
`;

// TODO Generalize once additional Workflows added
const WorkflowHelpDocsLink = () => (
  <span> For additional information and a guide to creating your first workflow, see our
    <StyledHelpDocsTextAndIcon href={'https://support.trustar.co/article/ehzg7ou4oy-creating-an-intel-workflow'} target={'_blank'}>
      <Icon icon={IconLookup.BOOK} />
    support documentation.
    </StyledHelpDocsTextAndIcon>
  </span>
)

export default WorkflowDetails;
